import { z } from "zod";

// Mirrors camel-server `test_reports/models.py` enums.
const testCaseTypeSchema = z.enum(["temporal_workflow", "rest_api_call", "local_script"]);

export type TestCaseType = z.infer<typeof testCaseTypeSchema>;

export const TEST_CASE_TYPE_LABELS: Record<TestCaseType, string> = {
  temporal_workflow: "Temporal Workflow",
  rest_api_call: "REST API",
  local_script: "Local Script",
};

const artifactTypeSchema = z.enum(["json", "media", "text", "log"]);

export type ArtifactType = z.infer<typeof artifactTypeSchema>;

export const ARTIFACT_TYPE_LABELS: Record<ArtifactType, string> = {
  json: "JSON",
  media: "Media",
  text: "Text",
  log: "Log",
};

const artifactSchema = z.object({
  name: z.string(),
  type: artifactTypeSchema,
  data: z.record(z.unknown()).nullable().optional(),
  assetId: z.string().nullable().optional(),
  textContent: z.string().nullable().optional(),
});

export type Artifact = z.infer<typeof artifactSchema>;

const testCaseResultSchema = z.object({
  passed: z.boolean(),
  failureMessage: z.string().nullable().optional(),
});

const testCaseSchema = z.object({
  name: z.string(),
  type: testCaseTypeSchema,
  result: testCaseResultSchema,
  durationMs: z.number().nullable().optional(),
  artifacts: z.array(artifactSchema).default([]),
  tags: z.array(z.string()).nullable().optional(),
});

export type TestCase = z.infer<typeof testCaseSchema>;

// createdAt comes back from camel-server as an ISO datetime string; we coerce
// to epoch millis so the UI can format consistently.
const createdAtSchema = z.coerce.date().transform((d) => d.getTime());

const baseSummaryShape = {
  id: z.string(),
  name: z.string(),
  commitHash: z.string(),
  description: z.string().nullable().optional(),
  createdAt: createdAtSchema,
  totalCount: z.number(),
  passedCount: z.number(),
  failedCount: z.number(),
  tags: z.array(z.string()).nullable().optional(),
};

const testReportSummarySchema = z.object(baseSummaryShape);

export const testReportResponseSchema = z.object({
  ...baseSummaryShape,
  testCases: z.array(testCaseSchema),
  metadata: z.record(z.unknown()).nullable().optional(),
});

export const listTestReportsResponseSchema = z.object({
  reports: z.array(testReportSummarySchema),
});
